import { useMsal } from '@azure/msal-react';
import {
    Button,
    createTableColumn,
    DataGridCell,
    DataGridHeaderCell,
    Dropdown,
    makeStyles,
    Option,
    SearchBox,
    Spinner,
    tokens,
    type TableColumnDefinition,
} from '@fluentui/react-components';
import { Add24Regular, Open24Regular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelExportButton from '../components/ExcelExportButton';
import ProjectCreateDrawer from '../components/ProjectCreateDrawer';
import '../index.css';
import type { ProjektItem } from '../models/ProjektItem';
import { DataService } from '../services/dataService';
import useConfigStore from '../store/config';
import type { ExcelExportColumn } from '../utils/excelExport';

const useStyles = makeStyles({
    container: {
        padding: `0 ${tokens.spacingHorizontalL}`,
        height: '100%',
        overflow: 'auto',
    },
    dataGridHeader: {
        backgroundColor: tokens.colorNeutralBackground3,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        textAlign: 'center',
    },
});
const ROW_HEIGHT = 40; // px

// Proportional weights for dynamic resizing (higher = wider)
const COLUMN_WEIGHTS: Record<string, number> = {
    action: 0.5,
    title: 2.5,
    status: 1,
    projektleiterName: 1.2,
    kostenstellenNummer: 1,
    projektID: 0.8,
    department: 1.5,
    departmentDepartment: 2,
    projektart: 1,
};

const COLUMN_ORDER = [
    'action',
    'title',
    'status',
    'projektleiterName',
    'kostenstellenNummer',
    'projektID',
    'department',
    'departmentDepartment',
    'projektart',
] as const;

const Dashboard: React.FC = () => {
    const { instance, accounts } = useMsal();
    const accountInfo = accounts[0] || null;
    const styles = useStyles();
    const isDashboardRoot = location.pathname === '/';
    const navigate = useNavigate();

    // Get the currently selected role and view type from the config store
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const dashboardViewType = useConfigStore((state) => state.dashboardViewType);
    const setDashboardViewType = useConfigStore((state) => state.setDashboardViewType);

    const dataService = React.useMemo(() => {
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accountInfo]);

    const [items, setItems] = useState([] as ProjektItem[]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterText, setFilterText] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [departmentFilter, setDepartmentFilter] = useState('All');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Get user info and available roles from config store
    const userInfo = useConfigStore((state) => state.userConfig.userInfo);
    const availableRoles = useConfigStore((state) => state.roleConfig.availableRoles);

    // Mapping for view type display text
    const viewTypeLabels: Record<string, string> = {
        myProjects: 'My Projects',
        myInstitute: 'My Institute',
        allProjects: 'All Projects',
    };

    useEffect(() => {
        const handleRoleChange = () => {
            console.log('Dashboard detected role change event from sessionStorage');
            // Roles are now managed by the store, so we just log the event
        };

        window.addEventListener('role-changed', handleRoleChange);
        window.addEventListener('storage', handleRoleChange);

        return () => {
            window.removeEventListener('role-changed', handleRoleChange);
            window.removeEventListener('storage', handleRoleChange);
        };
    }, []);

    // Get unique statuses and departments for dropdown filters
    const uniqueStatuses = ['All', ...new Set(items.map((item) => item.projektstatusInternBezeichnung).filter(Boolean))];
    const uniqueDepartments = ['All', ...new Set(items.map((item) => item.department).filter(Boolean))];
    console.log('Unique statuses:', uniqueStatuses);

    // Filter items based on search text and status/department filters
    const filteredItems = items.filter((item) => {
        const matchesText =
            !filterText ||
            item.title?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.projektleiterName?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.projektID?.toString().includes(filterText) ||
            item.department?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.departmentDepartment?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.kostenstellenNummer?.toString().includes(filterText);

        const matchesStatus = statusFilter === 'All' || item.projektstatusInternBezeichnung === statusFilter;
        const matchesDepartment = departmentFilter === 'All' || item.department === departmentFilter;

        return matchesText && matchesStatus && matchesDepartment;
    });

    // Sort items if a sort column is selected
    const sortedItems = React.useMemo(() => {
        if (!sortColumn) return filteredItems;

        const sorted = [...filteredItems].sort((a, b) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const aValue = (a as any)[sortColumn];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const bValue = (b as any)[sortColumn];

            // Handle null/undefined values
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
            if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

            // Handle string comparison
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            // Handle number comparison
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Fallback to string comparison
            return sortDirection === 'asc'
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });

        return sorted;
    }, [filteredItems, sortColumn, sortDirection]);

    const handleSort = (columnId: string) => {
        if (sortColumn === columnId) {
            // Toggle sort direction if clicking the same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new sort column and reset to ascending
            setSortColumn(columnId);
            setSortDirection('asc');
        }
    };

    const handleOpenProject = (item: ProjektItem) => {
        if (item.projektID) {
            // Set navigation origin to dashboard
            sessionStorage.setItem('navigationOrigin', 'dashboard');
            navigate(`/project/${item.projektID}`);
        }
    };

    // Calculate dynamic grid height (3/4 of window height)
    const gridHeight = React.useMemo(() => {
        return Math.max(windowHeight * 0.65, 300); // Min 300px
    }, [windowHeight]);

    // Calculate total weight for proportional sizing
    const totalWeight = React.useMemo(() => {
        return COLUMN_ORDER.reduce((sum, columnId) => {
            return sum + (COLUMN_WEIGHTS[columnId] || 1);
        }, 0);
    }, []);

    // Calculate dynamic column widths as percentages based on proportional weights
    const getColumnWidth = React.useCallback((index: number) => {
        const columnId = COLUMN_ORDER[index];
        const weight = COLUMN_WEIGHTS[columnId] || 1;

        // Calculate percentage width based on weight proportion
        const percentageWidth = (weight / totalWeight) * 100;

        return percentageWidth;
    }, [totalWeight]);


    const columns: TableColumnDefinition<ProjektItem>[] = [
        createTableColumn<ProjektItem>({
            columnId: 'action',
            renderHeaderCell: () => '',
            renderCell: (item) => item.linkToProject ? (
                <a href={item.linkToProject} target="_blank" rel="noopener noreferrer" title="Open link">
                    <Open24Regular style={{ cursor: 'pointer', color: tokens.colorBrandForeground1 }} />
                </a>
            ) : null,
        }),
        createTableColumn<ProjektItem>({
            columnId: 'title',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('title')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'title' ? 'bold' : 'normal' }}>
                    Title {sortColumn === 'title' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.title || '-',
        }),
        createTableColumn<ProjektItem>({
            columnId: 'status',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('projektStatusAsText')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektStatusAsText' ? 'bold' : 'normal' }}>
                    Status {sortColumn === 'projektStatusAsText' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.projektstatusInternBezeichnung || '-',
        }),
        createTableColumn<ProjektItem>({
            columnId: 'projektleiterName',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('projektleiterName')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektleiterName' ? 'bold' : 'normal' }}>
                    Project Manager {sortColumn === 'projektleiterName' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.projektleiterName || '-',
        }),
        createTableColumn<ProjektItem>({
            columnId: 'kostenstellenNummer',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('kostenstellenNummer')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'kostenstellenNummer' ? 'bold' : 'normal' }}>
                    Cost Carrier Number {sortColumn === 'kostenstellenNummer' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.kostenstellenNummer || '-',
        }),
        createTableColumn<ProjektItem>({
            columnId: 'projektID',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('projektID')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektID' ? 'bold' : 'normal' }}>
                    Project ID {sortColumn === 'projektID' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.projektID?.toString() || '-',
        }),
        createTableColumn<ProjektItem>({
            columnId: 'department',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('department')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'department' ? 'bold' : 'normal' }}>
                    Department {sortColumn === 'department' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.department || '-',
        }),
        createTableColumn<ProjektItem>({
            columnId: 'departmentDepartment',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('departmentDepartment')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'departmentDepartment' ? 'bold' : 'normal' }}>
                    Department (Detail) {sortColumn === 'departmentDepartment' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.departmentDepartment || '-',
        }),
        createTableColumn<ProjektItem>({
            columnId: 'projektart',
            renderHeaderCell: () => (
                <div onClick={() => handleSort('projektart')} style={{ cursor: 'pointer', fontWeight: sortColumn === 'projektart' ? 'bold' : 'normal' }}>
                    Project Type {sortColumn === 'projektart' && (sortDirection === 'asc' ? '▲' : '▼')}
                </div>
            ),
            renderCell: (item) => item.projektart || '-',
        }),
    ];

    const excelColumns: ExcelExportColumn<ProjektItem>[] = [
        { header: 'Title', value: (item) => item.title, width: 50 },
        { header: 'Status', value: (item) => item.projektstatusInternBezeichnung },
        { header: 'Project Manager', value: (item) => item.projektleiterName, width: 25 },
        { header: 'Cost Carrier Number', value: (item) => item.kostenstellenNummer },
        { header: 'Project ID', value: (item) => item.projektID, width: 12 },
        { header: 'Department', value: (item) => item.department, width: 30 },
        { header: 'Department (Detail)', value: (item) => item.departmentDepartment, width: 40 },
        { header: 'Project Type', value: (item) => item.projektart },
    ];

    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const onCloseCreateDrawer = () => {
        setIsDrawerOpen(false);
        // Refresh projects when drawer closes
        setRefreshTrigger(prev => prev + 1);
    };

    React.useEffect(() => {
        const handleWindowResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    React.useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const fetchItems = async () => {
            // Clear items and show loading spinner when starting to fetch for a new view

            console.log('DEBUG: fetchItems called with dashboardViewType=', dashboardViewType, 'dataService=', !!dataService, 'userInfo=', userInfo);

            if (!dataService) {
                console.log('DEBUG: No dataService, returning');
                setIsLoading(true);
                return;
            }

            // For myInstitute view, we need userInfo to be available
            if (dashboardViewType === 'myInstitute' && !userInfo) {
                console.log('DEBUG: Waiting for userInfo before fetching projects for myInstitute view');
                setIsLoading(true);
                return;
            }
            try {
                // Fetch projects based on view type
                let response: ProjektItem[] = [];
                console.log('DEBUG: Starting project fetch for view type:', dashboardViewType);

                if (dashboardViewType === 'myProjects') {
                    console.log('DEBUG: Fetching myProjects - calling API...');
                    try {
                        response = await dataService.getProjektListeForCurrentUser(signal);
                        console.log('DEBUG: myProjects API call returned:', response?.length, 'items');
                    } catch (apiError) {
                        console.error('DEBUG: myProjects API call failed:', apiError);
                        throw apiError;
                    }
                } else if (dashboardViewType === 'myInstitute') {
                    // Using instituteId from userInfo and studienjahr 2025
                    const institutId = (userInfo?.departmentId || 0);
                    console.log('DEBUG: Fetching myInstitute with institutId=', institutId, '- calling API...');
                    try {
                        response = await dataService.getProjektListeWithoutDetailsByInstitution(institutId, 2025, signal);
                        console.log('DEBUG: myInstitute API call returned:', response?.length, 'items');
                    } catch (apiError) {
                        console.error('DEBUG: myInstitute API call failed:', apiError);
                        throw apiError;
                    }
                } else if (dashboardViewType === 'allProjects') {
                    console.log('DEBUG: Fetching allProjects - calling API...');
                    try {
                        response = await dataService.getProjektListe(false, signal);
                        console.log('DEBUG: allProjects API call returned:', response?.length, 'items');
                    } catch (apiError) {
                        console.error('DEBUG: allProjects API call failed:', apiError);
                        throw apiError;
                    }
                }
                console.log('Fetched projects for dashboardViewType', dashboardViewType, 'items count:', response?.length || 0);
                if (response) {
                    console.log('DEBUG: Setting items state with', response.length, 'items');
                    console.log('DEBUG: Response data:', response);
                    setItems(response);
                    setIsLoading(false);
                }
            } catch (error) {
                // Ignore abort errors - they occur when switching views before the request completes
                if (error instanceof Error && error.name === 'AbortError') {
                    console.log('Request was cancelled due to view change');
                } else {
                    console.error('ERROR: Failed to fetch items:', error);
                }
            }
        };
        setItems([]);
        setIsLoading(true);
        fetchItems();

        // Cleanup: abort the request if the component unmounts or dependencies change
        return () => {
            abortController.abort();
        };
    }, [dataService, dashboardViewType, userInfo, refreshTrigger]);

    return (
        <>
            {isDashboardRoot && userInfo && availableRoles.length > 0 && (
                <div className={styles.container}>
                    <h1>Welcome to IMCProjects Dashboard</h1>
                    {userInfo && (
                        <p style={{ marginBottom: '20px' }}>
                            DEBUG: Logged in as: <strong>{userInfo.userId}</strong> ({userInfo.upn}, {userInfo.departmentId})
                        </p>
                    )}
                    <div className="searchContainer" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '20px' }}>
                        <Dropdown
                            placeholder="View"
                            value={viewTypeLabels[dashboardViewType]}
                            onOptionSelect={(_e, option) =>
                                setDashboardViewType(option.optionValue as 'myProjects' | 'myInstitute' | 'allProjects' || 'myProjects')
                            }
                            style={{ width: '200px' }}
                        >
                            <Option value="myProjects">My Projects</Option>
                            <Option value="myInstitute">My Institute</Option>
                            {(selectedRole === 'Administratoren' || userInfo?.isUserInAnyPrivilegedGroup) && (
                                <Option value="allProjects">All Projects</Option>
                            )}
                        </Dropdown>
                        <Button
                            icon={<Add24Regular />}
                            appearance="primary"
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            New Project
                        </Button>
                    </div>
                    {isLoading && (
                        <div className={styles.loadingContainer}>
                            <Spinner label="Loading projects..." labelPosition="below" />
                        </div>
                    )}
                    {(!isLoading && dataService && items.length === 0) && (
                        <div className={styles.emptyStateContainer}>
                            <h2 style={{ color: tokens.colorNeutralForeground3 }}>
                                No projects available
                            </h2>
                            <p
                                style={{
                                    fontSize: '16px',
                                    color: tokens.colorNeutralForeground3,
                                    maxWidth: '500px',
                                }}
                            >
                                There are currently no projects assigned to you.
                            </p>
                        </div>
                    )}
                    {(!isLoading && dataService && items.length > 0) && (
                        <>
                            <div className="dropdownContainer" style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginBottom: '20px' }}>
                                <SearchBox
                                    placeholder="Search by title, project manager, project ID..."
                                    onChange={(_, newValue) => setFilterText(newValue?.value || '')}
                                    className="searchBox"
                                    value={filterText}
                                    style={{ flex: 1 }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '500', color: tokens.colorNeutralForeground1 }}>
                                        Status
                                    </label>
                                    <Dropdown
                                        placeholder="All"
                                        value={statusFilter}
                                        onOptionSelect={(_e, option) =>
                                            setStatusFilter(option.optionValue || 'All')
                                        }
                                        style={{ width: '250px' }}
                                    >
                                        {uniqueStatuses.map((status) => (
                                            <Option key={status} value={status}>
                                                {status}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '500', color: tokens.colorNeutralForeground1 }}>
                                        Department
                                    </label>
                                    <Dropdown
                                        placeholder="All"
                                        value={departmentFilter}
                                        onOptionSelect={(_e, option) =>
                                            setDepartmentFilter(option.optionValue || 'All')
                                        }
                                        style={{ width: '250px' }}
                                    >
                                        {uniqueDepartments.map((dept) => (
                                            <Option key={dept} value={dept}>
                                                {dept}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                                <ExcelExportButton
                                    items={sortedItems}
                                    columns={excelColumns}
                                    fileName={`Projects_${new Date().toISOString().split('T')[0]}`}
                                    sheetName="Projects"
                                />
                            </div>
                            {sortedItems.length === 0 ? (
                                <div className={styles.emptyStateContainer}>
                                    <h2 style={{ color: tokens.colorNeutralForeground3 }}>
                                        Keine Projekte gefunden
                                    </h2>
                                    <p
                                        style={{
                                            fontSize: '16px',
                                            color: tokens.colorNeutralForeground3,
                                            maxWidth: '500px',
                                        }}
                                    >
                                        Keine Projekte entsprechen Ihren Filterkriterien.
                                    </p>
                                </div>
                            ) : (
                                <div className="dataGridContainer" ref={containerRef} style={{ overflowX: 'auto' }}>
                                    {/* Header Row */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            width: '100%',
                                            backgroundColor: tokens.colorNeutralBackground3,
                                            borderBottom: '1px solid #d1d5db',
                                            fontWeight: 600,
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 10,
                                        }}
                                    >
                                        {COLUMN_ORDER.map((columnId) => {
                                            const column = columns.find((col) => col.columnId === columnId);
                                            if (!column) return null;
                                            const width = getColumnWidth(COLUMN_ORDER.indexOf(columnId));
                                            return (
                                                <DataGridHeaderCell
                                                    key={columnId}
                                                    className="dataGridHeaderCell"
                                                    style={{
                                                        flex: `0 0 ${width}%`,
                                                        paddingLeft: '8px',
                                                        paddingRight: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        borderRight: '1px solid #e1e1e1',
                                                        boxSizing: 'border-box',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {column.renderHeaderCell()}
                                                </DataGridHeaderCell>
                                            );
                                        })}
                                    </div>

                                    {/* Virtual Grid */}
                                    <div style={{ height: gridHeight, width: '100%', overflowY: 'auto' }}>
                                        {sortedItems.map((item, rowIndex) => (
                                            <div
                                                key={rowIndex}
                                                style={{
                                                    display: 'flex',
                                                    height: ROW_HEIGHT,
                                                    borderBottom: '1px solid #e1e1e1',
                                                }}
                                            >
                                                {COLUMN_ORDER.map((columnId) => {
                                                    const column = columns.find((col) => col.columnId === columnId);
                                                    const width = getColumnWidth(COLUMN_ORDER.indexOf(columnId));
                                                    if (!column) return null;
                                                    return (
                                                        <DataGridCell
                                                            key={columnId}
                                                            style={{
                                                                flex: `0 0 ${width}%`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                paddingLeft: '8px',
                                                                paddingRight: '8px',
                                                                borderRight: '1px solid #e1e1e1',
                                                                boxSizing: 'border-box',
                                                                cursor: 'pointer',
                                                                overflow: 'hidden',
                                                            }}
                                                            onClick={() => handleOpenProject(item)}
                                                            role="gridcell"
                                                        >
                                                            {column.renderCell(item)}
                                                        </DataGridCell>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            {isDrawerOpen && <ProjectCreateDrawer
                isOpen={isDrawerOpen}
                onClose={() => onCloseCreateDrawer()
                }
                userInfo={userInfo}
                dataService={dataService!}
            />}
        </>
    );
};
export default Dashboard;
